<template>
  <Sidesheet
    :title="title"
    :active="active"
    @close="close"
  >
    <template #actions>
      <Button
        v-if="canEdit && isEditing"
        label="Удалить"
        size="small"
        :icon="{
          icon: 'trash',
          color: 'secondary',
        }"
        @click="remove"
      />
    </template>
    <Loader
      v-show="!isInitialized && busy"
      class="loader"
      label="Данные загружаются"
      height="300"
      :active="true"
    />
    <Scroll
      v-show="isInitialized"
      class="scroll"
      :with-top-scroller="true"
    >
      <div class="shadow-separator shadow-separator--top" />
      <vee-observer
        ref="veeObserver"
        v-slot="{ handleSubmit, errors }"
        slim
      >
        <form
          class="form"
          @submit.prevent="handleSubmit(submit)"
        >
          <div class="content">
            <div class="input-group">
              <div class="input-group__item input-group__item--8">
                <Switcher
                  v-model="formData.type"
                  class="h-switcher"
                  type="group"
                  :items="typesList"
                />
              </div>
              <template v-if="formData.type === 'address'">
                <vee-provider
                  class="input-group__item input-group__item--2"
                  vid="address[name]"
                  rules="max:255"
                  name=" "
                >
                  <Input
                    v-model="formData.address.name"
                    floating-label="Название адреса доставки"
                    :error="errors['address[name]']"
                    type="text"
                    :clearable="true"
                    :actually-normal-error-messages="true"
                  />
                </vee-provider>
                <div class="input-group__item input-group__item--6" />
                <vee-provider
                  class="input-group__item input-group__item--3"
                  vid="address[city]"
                  rules="required|max:500"
                  name=" "
                >
                  <Input
                    v-model="formData.address.city"
                    floating-label="Город"
                    :items="lists.cityList"
                    type="select"
                    :clearable="true"
                    :required-mark="true"
                    :actually-normal-error-messages="true"
                    :searchable="true"
                  />
                </vee-provider>
                <vee-provider
                  class="input-group__item input-group__item--3"
                  vid="address[street]"
                  name=" "
                >
                  <Input
                    v-model="formData.address.street"
                    floating-label="Улица"
                    :items="lists.streetList"
                    type="select"
                    :clearable="true"
                    :actually-normal-error-messages="true"
                    :searchable="true"
                  />
                </vee-provider>
                <vee-provider
                  class="input-group__item input-group__item--1"
                  vid="address[home]"
                  rules="max:10"
                  message=" "
                >
                  <Input
                    v-model="formData.address.home"
                    floating-label="Дом"
                    :error="errors['address[home]']"
                    type="text"
                    :clearable="true"
                    :actually-normal-error-messages="true"
                  />
                </vee-provider>
                <vee-provider
                  class="input-group__item input-group__item--1"
                  vid="address[housing]"
                  rules="max:10"
                  name=" "
                >
                  <Input
                    v-model="formData.address.housing"
                    floating-label="Стр./корп."
                    :error="errors['address[housing]']"
                    type="text"
                    :clearable="true"
                    :actually-normal-error-messages="true"
                  />
                </vee-provider>
              </template>
              <template v-else>
                <vee-provider
                  class="input-group__item input-group__item--2"
                  vid="zone[name]"
                  rules="required|max:255"
                  name=" "
                >
                  <Input
                    v-model="formData.zone.name"
                    floating-label="Название зоны доставки"
                    :error="errors['zone[name]']"
                    type="text"
                    :clearable="true"
                    :required-mark="true"
                    :actually-normal-error-messages="true"
                  />
                </vee-provider>
                <vee-provider
                  class="input-group__item input-group__item--6"
                  vid="zone[url]"
                  rules="required|max:1024"
                  name=" "
                >
                  <Input
                    v-model="formData.zone.url"
                    floating-label="URL зоны доставки"
                    :error="errors['zone[url]']"
                    type="text"
                    :editable="true"
                    :clearable="true"
                    :required-mark="true"
                    :copied-to-clipboard="true"
                    :actually-normal-error-messages="true"
                    @copy="onUrlCopy"
                  />
                </vee-provider>
              </template>
              <div class="input-group__item input-group__item--2">
                <Input
                  v-model="formData.filial"
                  floating-label="Филиал"
                  :items="lists.filials"
                  type="select"
                  :clearable="true"
                  :searchable="true"
                />
              </div>
              <div class="input-group__item input-group__item--6">
                <Input
                  v-model="formData.positions"
                  floating-label="Должности"
                  :items="lists.positions"
                  type="select"
                  :multiple="true"
                  :clearable="true"
                  :searchable="true"
                />
              </div>
              <div class="input-group__item input-group__item--4">
                <Input
                  v-model="formData.paymentTypes"
                  floating-label="Типы оплаты"
                  :items="lists.paymentTypes"
                  type="select"
                  :multiple="true"
                  :clearable="true"
                  :searchable="true"
                />
              </div>
            </div>
            <div class="subtitle">
              Период действия
            </div>
            <div class="input-group">
              <div class="input-group__item input-group__item--2">
                <Input
                  v-model="formData.period.type"
                  floating-label="Период действия"
                  :items="lists.periodTypes"
                  type="select"
                  :clearable="true"
                  :searchable="true"
                />
              </div>
              <div
                v-if="!!formData.period.type"
                class="input-group__item input-group__item--6"
              >
                <div class="period">
                  <div class="period__control">
                    <vee-provider
                      v-if="formData.period.type.value === 'week'"
                      vid="period[week]"
                      rules="required"
                      name=" "
                    >
                      <WeekDays
                        v-model="formData.period.week"
                        :error="getPeriodControlError('period[week]', errors)"
                      />
                    </vee-provider>
                    <vee-provider
                      v-if="formData.period.type.value === 'month'"
                      vid="period[month]"
                      rules="required"
                      name=" "
                    >
                      <MonthDays
                        v-model="formData.period.month"
                        :error="getPeriodControlError('period[month]', errors)"
                        :invalid="isPeriodControlValid('period[month]', errors)"
                      />
                    </vee-provider>
                    <vee-provider
                      v-if="formData.period.type.value === 'year'"
                      vid="period[year]"
                      rules="required"
                      name=" "
                    >
                      <DateSelect
                        v-model="formData.period.year"
                        :error="getPeriodControlError('period[year]', errors)"
                        :invalid="isPeriodControlValid('period[year]', errors)"
                      />
                    </vee-provider>
                    <vee-provider
                      v-if="formData.period.type.value === 'random'"
                      vid="period[random]"
                      rules="required"
                      name=" "
                    >
                      <DateSelect
                        v-model="formData.period.random"
                        :error="getPeriodControlError('period[random]', errors)"
                        :invalid="isPeriodControlValid('period[random]', errors)"
                        :with-year="true"
                      />
                    </vee-provider>
                    <vee-provider
                      v-if="formData.period.type.value === 'range'"
                      class="period__dh"
                      vid="period[range]"
                      rules="required"
                      name=" "
                    >
                      <Input
                        ref="rangePicker"
                        v-model="formData.period.range"
                        :error="errors['period[range]']"
                        placeholder="00.00.0000 - 00.00.0000"
                        type="period-without-fixed-intervals"
                        icon="calendar"
                        :icon-right="true"
                        :actually-normal-error-messages="true"
                      />
                    </vee-provider>
                  </div>
                  <div class="period__time">
                    <div class="label">
                      Время действия, с – по
                    </div>
                    <vee-provider
                      vid="time[from]"
                      rules="time"
                      name=" "
                    >
                      <Input
                        v-model="formData.time.from"
                        placeholder="00:00"
                        mask="##:##"
                        :error="errors['time[from]']"
                        type="text"
                        :clearable="true"
                        :required-mark="true"
                        :actually-normal-error-messages="true"
                      />
                    </vee-provider>
                    <vee-provider
                      vid="time[to]"
                      rules="time"
                      name=" "
                    >
                      <Input
                        v-model="formData.time.to"
                        placeholder="00:00"
                        mask="##:##"
                        :error="errors['time[to]']"
                        type="text"
                        :clearable="true"
                        :required-mark="true"
                        :actually-normal-error-messages="true"
                      />
                    </vee-provider>
                  </div>
                </div>
              </div>
            </div>
            <div class="subtitle">
              Настройки положения
              <small class="label">
                Введите координаты Google
              </small>
            </div>
            <div class="input-group">
              <vee-provider
                class="input-group__item input-group__item--1"
                vid="coords[lat]"
                rules="max:10"
                name=" "
              >
                <Input
                  v-model="formData.coords.lat"
                  floating-label="Широта"
                  :error="errors['coords[lat]']"
                  type="price-number"
                  :allow-negative="true"
                  :fractional-length="Infinity"
                  :actually-normal-error-messages="true"
                />
              </vee-provider>
              <vee-provider
                class="input-group__item input-group__item--1"
                vid="coords[lon]"
                rules="max:10"
                name=" "
              >
                <Input
                  v-model="formData.coords.lon"
                  floating-label="Долгота"
                  :error="errors['coords[lon]']"
                  type="price-number"
                  :allow-negative="true"
                  :fractional-length="Infinity"
                  :actually-normal-error-messages="true"
                />
              </vee-provider>
              <div class="input-group__item input-group__item--3">
                <div class="incdec-field">
                  <div class="incdec-field__label label">
                    Изменить дистанцию, если адрес есть в маршруте
                  </div>
                  <div class="incdec-field__controls">
                    <div class="incdec-field__switcher">
                      <Switcher
                        v-model="formData.distance.change"
                        class="h-switcher"
                        type="group"
                        :items="incdecValuesList"
                      />
                    </div>
                    <vee-provider
                      class="incdec-field__input"
                      vid="distance[value]"
                      rules="max:10"
                      name=" "
                    >
                      <Input
                        v-model="formData.distance.value"
                        placeholder="0"
                        :error="errors['distance[value]']"
                        type="price-number"
                        :clearable="true"
                        :fractional-length="Infinity"
                        :actually-normal-error-messages="true"
                      />
                    </vee-provider>
                    <div class="incdec-field__unit">
                      км
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="subtitle">
              Настройки оплаты
            </div>
            <div class="input-group">
              <div class="input-group__item input-group__item--3">
                <div class="incdec-field">
                  <div class="incdec-field__label label">
                    Изменить оплату, если адрес есть в маршруте
                  </div>
                  <div class="incdec-field__controls">
                    <div class="incdec-field__switcher">
                      <Switcher
                        v-model="formData.price.change"
                        class="h-switcher"
                        type="group"
                        :items="incdecValuesList"
                      />
                    </div>
                    <vee-provider
                      class="incdec-field__input"
                      vid="price[value]"
                      rules="max:10"
                      name=" "
                    >
                      <Input
                        v-model="formData.price.value"
                        placeholder="0"
                        :error="errors['price[value]']"
                        type="price-number"
                        :clearable="true"
                        :fractional-length="Infinity"
                        :actually-normal-error-messages="true"
                      />
                    </vee-provider>
                    <div class="incdec-field__unit incdec-field__unit--rub">
                      ฿
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="shadow-separator shadow-separator--bottom" />
          <div class="actions">
            <template v-if="canEdit">
              <Button
                label="Отменить"
                size="large"
                @click="close"
              />
              <Button
                label="Сохранить"
                color="primary"
                type="submit"
                size="large"
                :disabled="getIsSaveDisabled"
              />
            </template>
            <Button
              v-else
              label="Закрыть"
              size="large"
              @click="close"
            />
          </div>
        </form>
      </vee-observer>
    </Scroll>
  </Sidesheet>
</template>

<script>
import deepcopy from "deepcopy";

import Sidesheet from "@/components/sidesheet.vue";
import Loader from "@/components/loader.vue";
import Scroll from "@/components/shadow-scroll-container.vue";
import Input from "@/components/controls2/Input.vue";
import Button from "@/components/controls2/Button.vue";
import Switcher from "@/components/controls2/Switcher.vue";
import WeekDays from "@/components/week-days.vue";
import MonthDays from "@/components/month-days.vue";
import DateSelect from "@/components/date-select.vue";

import api from "@/plugins/api";
import { getRange } from "@/utils/date/ranges";

export default {
  components: {
    Sidesheet,
    Loader,
    Scroll,
    Input,
    Button,
    Switcher,
    WeekDays,
    MonthDays,
    DateSelect,
  },
  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isInitialized: false,
    busy: false,
    active: false,
    id: null,
    formData: {
      type: "address",
      address: {
        name: null,
        city: null,
        street: null,
        home: null,
        housing: null,
      },
      zone: {
        name: null,
        url: null,
      },
      filial: null,
      positions: [],
      paymentTypes: [],
      period: {
        type: null,
        week: [],
        month: [],
        year: [],
        random: [],
        range: {
          from: null,
          to: null,
        },
      },
      time: {
        from: null,
        to: null,
      },
      coords: {
        lat: null,
        lon: null,
      },
      distance: {
        value: null,
        change: "up",
      },
      price: {
        value: null,
        change: "up",
      },
    },
    formDataDefaults: {},
    lists: {
      periodTypes: [
        {
          label: "Каждую неделю",
          value: "week",
        },
        {
          label: "Каждый месяц",
          value: "month",
        },
        {
          label: "Каждый год",
          value: "year",
        },
        {
          label: "Произвольные дни",
          value: "random",
        },
        {
          label: "Период",
          value: "range",
        },
      ],
      filials: [],
      positions: [],
      paymentTypes: [],
      cityList: [],
      streetList: [],
      buildingList: [],
    },
    isFirstRender: true
  }),
  computed: {
    isEditing () {
      return !!this.id;
    },
    title () {
      if (!this.canEdit) {
        return "Просмотр адреса";
      }

      if (this.isEditing) {
        return "Редактирование адреса";
      }

      return "Новый адрес";
    },
    typesList () {
      return [
        {
          label: "Адрес доставки",
          value: "address",
        },
        {
          label: "Зона доставки",
          value: "zone",
        },
      ];
    },
    incdecValuesList () {
      return [
        {
          label: "Увеличить",
          value: "up",
        },
        {
          label: "Уменьшить",
          value: "down"
        },
      ];
    },
    formDataToSubmit () {
      const data = deepcopy(this.formData);

      if (data.filial) {
        data.filial = data.filial.value;
      }

      if (data.address.city && data.address.city.value) {
        data.address.city = data.address.city.value;
      }

      if (data.address.street && data.address.street.value) {
        data.address.street = data.address.street.value;
      }

      if (data.positions && data.positions.length) {
        data.positions = data.positions.map(({ value }) => value);
      }

      if (data.paymentTypes && data.paymentTypes.length) {
        data.paymentTypes = data.paymentTypes.map(({ value }) => value);
      }

      const periodType = data.period.type ? data.period.type.value : null;

      if (periodType) {
        data.period.type = periodType;

        if (["year", "random"].includes(periodType)) {
          data.period[periodType] = data.period[periodType].map(({ date }) => date);
        }

        if (periodType === "range") {
          const rangeParts = String(data.period.range || "").split(" - ");

          data.period.range = {
            from: rangeParts[0] || null,
            to: rangeParts[1] || null,
          };
        }
      }

      return data;
    },
    getIsSaveDisabled () {
      // return this.busy || !this.formData.address.city || (this.lists.streetList.length > 0 && !this.formData.address.street);
      return this.busy || (!this.formData.address.city && this.formData.type === 'address');
    }
  },
  watch: {
    "formData.address.city": async function() {
      if(typeof this.formData.address.city === 'string') {
        this.formData.address.city = {
          value: this.formData.address.city,
          label: this.formData.address.city,
        };
        return;
      }

      if(this.formData.address.city && this.lists.cityList.length > 0 && !this.lists.cityList.find(city => city.value === this.formData.address.city.value)) {
        this.formData.address.city = null;
        this.formData.address.street = null;
        return;
      }

      const streetList = this.formData.address.city && await this.$store.dispatch("filters/loadStreets", this.formData.address.city.value);

      if(streetList && streetList.length > 0) {
        this.lists.streetList = streetList.filter(street => street.value !== '');
      }

      if(!streetList.find(street => street.value === this.formData.address.street.value)) {
        this.formData.address.street = null;
      }
    },

    "formData.address.street": async function() {
      if(typeof this.formData.address.street === 'string') {
        this.formData.address.street = {
          value: this.formData.address.street,
          label: this.formData.address.street,
        };
      }

      if(this.formData.address.street && this.lists.streetList.length > 0 && !this.lists.streetList.find(street => street.value === this.formData.address.street.value)) {
        this.formData.address.street = null;
      }
    }
  },
  created () {
    this.formDataDefaults = deepcopy(this.formData);
  },
  methods: {
    async fetchLists () {
      this.lists = {
        ...this.lists,
        filials: await this.$store.dispatch("filters/loadAllFilials"),
        positions: await this.$store.dispatch("positions/getPositionsDriversList"),
        paymentTypes: await this.$store.dispatch("filters/loadPaytypes"),
      };

      const cityList = await this.$store.dispatch("filters/loadCities");
      if(cityList && cityList.length > 0) {
        this.lists.cityList = cityList.filter(city => city.value !== '');
      }
    },
    async fetchData () {
      const data = {
        ...this.formData,
        ...(await api.get("bad-address/view", {
          params: {
            id: this.id,
          },
        })).data,
      };

      if (data.filial) {
        data.filial = this.findValueInList(data.filial, "filials");
      }

      if (data.positions) {
        data.positions = this.findValueInList(data.positions, "positions", true);
      }

      if (data.paymentTypes) {
        data.paymentTypes = this.findValueInList(data.paymentTypes, "paymentTypes", true);
      }

      if (data.period.type) {
        data.period.type = this.findValueInList(data.period.type, "periodTypes");
      }

      this.formData = data;
    },
    async open (id = null) {
      this.isInitialized = false;

      this.id = id;

      this.formData = deepcopy(this.formDataDefaults);

      this.$refs.veeObserver.reset();

      this.active = true;

      this.$el.focus();

      this.busy = true;

      try {
        await this.fetchLists();

        if (id) {
          await this.fetchData();
        }
      } catch (error) {
        console.error(error);
        this.close();
      }

      this.busy = false;
      this.isInitialized = true;

      this.setPeriodRange();

      // console.log('пришел город', this.formData.address.city);
      // console.log('hпилла уоичца->', this.formData.address.street);
      // console.log('list-->', this.lists.streetList);
      // console.log('citylist-->', this.lists.cityList);
    },
    close () {
      this.active = false;
      this.$emit("close");
    },
    remove () {
      this.$emit("remove", this.id);
    },
    findValueInList(value, list, multiple = false) {
      const target = this.lists[list];

      if (!target) {
        return null;
      }

      if (multiple && Array.isArray(value)) {
        return this.lists[list].filter(item => value.includes(item.value));
      }

      return this.lists[list].find(item => item.value === value);
    },
    setPeriodRange () {
      const { period } = this.formData;

      if (!period.type || period.type.value !== "range") {
        return;
      }

      const { from = null, to = null } = period.range;

      this.$refs.rangePicker.period.start.input = from;
      this.$refs.rangePicker.period.end.input = to;
      this.$refs.rangePicker.dateValue = getRange(from, to);
    },
    getPeriodControlError (key, errors = []) {
      return errors[key] && errors[key].length ? errors[key].join("; ") : false;
    },
    isPeriodControlValid (key, errors = []) {
      return errors[key] && errors[key].length;
    },
    async submit () {
      if (!this.canEdit) {
        return;
      }

      const { isEditing } = this;
      const action = isEditing ? "update" : "create";

      this.busy = true;

      try {
        const result = await api({
          method: isEditing ? "PUT" : "POST",
          url: `bad-address/${action}`,
          params: {
            id: isEditing ? this.id : null,
          },
          data: this.formDataToSubmit,
        });

        this.$emit(action, { id: this.id, data: result.data });

        this.close();
      } catch ({ response }) {
        if (response.status === 400) {
          this.$refs.veeObserver.setErrors(response.data.errors || []);

          this.$notify({
            group: "error-message",
            data: {
              textMessage: `Ошибка, проверьте введенные данные`,
            },
          });
        }
      } finally {
        this.busy = false;
      }
    },
    onUrlCopy () {
      this.$notify({
        group: "success-message",
        data: {
          textMessage: "Ссылка успешно скопирована в буфер обмена!",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/hax";

@include sidesheet;

.input-group {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 24px 30px;
  max-width: 1030px;

  &__item {
    &--2 {
      grid-column: span 2;
    }

    &--3 {
      grid-column: span 3;
    }

    &--4 {
      grid-column: span 4;
    }

    &--6 {
      grid-column: span 6;
    }

    &--8 {
      grid-column: span 8;
    }

    @include onlyMobile {
      &--1 {
        grid-column: span 4;
      }

      &:not(&--1) {
        grid-column: span 8;
      }

      &:empty {
        display: none;
      }
    }
  }
}

.period {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  align-items: flex-start;

  @include onlyMobile {
    grid-template-columns: 1fr;
    gap: 30px 0;
  }

  &__control {
    display: flex;
    flex-direction: column;
    grid-column: span 2;
  }

  &__dh {
    padding-top: 23px;
  }

  &__time {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 30px;

    .label {
      grid-column: span 2;
      margin-bottom: 5px;
    }
  }
}

.subtitle {
  display: flex;
  flex-direction: column;
  margin-top: 36px;
  margin-bottom: 20px;
  font-weight: 500;
  font-size: 18px;
}

.label {
  color: $color-label;
  font-size: 12px;
}

.incdec-field {
  display: flex;
  flex-direction: column;

  &__label {
    width: 100%;
    margin-bottom: 10px;
  }

  &__controls {
    display: flex;
    align-items: flex-start;
    gap: 0 15px;

    @include onlyMobile {
      gap: 0 10px;
    }
  }

  &__switcher {
    margin-right: 15px;

    @include onlyMobile {
      margin-right: 8px;

      .h-switcher {
        padding-bottom: 0;
      }
    }
  }

  &__input {
    max-width: 90px;

    @include onlyMobile {
      max-width: none;
    }
  }

  &__unit {
    align-self: center;
    font-size: 15px;

    &--rub {
      font-size: 20px;
    }
  }
}
</style>
